import Guid from "common/values/guid/guid";

import axios, { AxiosHeaders, AxiosResponse, CanceledError, HttpStatusCode } from "axios";
import PaginatedResponse from "common/contracts/paginated-response";
import PaginationParameters from "common/contracts/pagination-parameters";
import { assertIsAxiosError } from "common/errors";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Individual from "marketplace/entities/individual/individual";
import ForumSubscriberInvitationApiRequest from "messaging/entities/forum-invitations/api/request-contracts/forum-subscriber-invitation-api-request";
import ForumInvitation, { ForumInvitationDoesNotExistError } from "messaging/entities/forum-invitations/forum-invitation";
import ForumAPIRequest from "messaging/entities/forum/api/request-contracts/forum-api-request";
import MessageParameters from "messaging/entities/forum/api/request-contracts/message-parameters";
import QueryForumsApiRequest from "messaging/entities/forum/api/request-contracts/query-forums-api-request";
import ForumAPIResponse from "messaging/entities/forum/api/response-contracts/forum-api-response";
import MarketplaceSubscriberInfoAPIResponse from "messaging/entities/forum/api/response-contracts/marketplace-subscriber-info-api-response";
import Forum, { ForumDoesNotExistError, UnauthorizedForumAccessException } from "messaging/entities/forum/forum";
import BulkMessageAPIRequest from "messaging/entities/message/api/request-contracts/bulk-message-api-request";
import CreateMessageAPIRequest from "messaging/entities/message/api/request-contracts/create-message-api-request";
import MessageAPIResponse from "messaging/entities/message/api/response-contracts/message-api-response";
import Message from "messaging/entities/message/message";
import AttachmentParameters from "messaging/values/attachment/api/request-contracts/attachment-parameters";
import MessageFileInfoAPIResponse from "messaging/values/attachment/api/response-contracts/message-file-info-api-response";
import MessageFileInfo from "messaging/values/attachment/message-file-info";
import SubscriberAPIRequest from "messaging/values/subcriber/api/request-contracts/subscriber-api-request";
import Session from "users/session/session";

export default class MessagingAPIService {
  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.authHeaders.concat({ 'Content-Type': 'application/json' });
  }

  private headersWithFormData(): AxiosHeaders {
    return this.authHeaders.concat({ 'Content-Type': 'multipart/form-data' });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a MessagingAPIService");
    this.authHeaders.set(
      "Authorization",
      `Bearer ${session.authToken.value}`
    );
  }

  async getForums(
    queryRequest: QueryForumsApiRequest,
    abortController?: AbortController
  ): Promise<Forum[]> {
    let url = new URL(
      "/messaging/forums",
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      url.search = queryRequest.asSearchParams().toString();
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      return response.data.map((forumData: Partial<ForumAPIResponse>) =>
        Object.assign(
          new ForumAPIResponse(),
          forumData
        ).deserialize()
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.BadRequest)
        throw new ForumDoesNotExistError(String(error.response?.data));
      if (error instanceof CanceledError)
        throw error;
      throw new MessagingAPIServiceError(
        "getForums",
        error
      );
    }
  }

  async getForumsByIds(
      ids: Guid[],
      abortController?: AbortController
  ): Promise<Forum[]> {
    let url = new URL(
        "/messaging/forums/by-ids",
        AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
          url.toString(),
          ids.map((id) => id.value),
          {
            headers: this.authHeaders,
            signal: abortController?.signal
          }
      );
      return response.data.map((forumData: Partial<ForumAPIResponse>) =>
          Object.assign(
              new ForumAPIResponse(),
              forumData
          ).deserialize()
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.BadRequest)
        throw new ForumDoesNotExistError(String(error.response?.data));
      if (error instanceof CanceledError)
        throw error;
      throw new MessagingAPIServiceError(
          "getForumsByIds",
          error
      );
    }
  }

  async getMessagesByForum(
    forum: Forum,
    query: MessageQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<Message>> {
    if (!forum.id) throw new MessagingAPIServiceError(
      "getMessagesByForum",
      new Error("Forum must have an id to get messages")
    );

    try {
      const url = new URL(
        `/messaging/forums/${forum.id.value}/messages`,
        AttorneyHubAPIService.apiBaseUrl,
      );
      query.asSearchParams().forEach((value, key) => {
        url.searchParams.append(key, value);
      });
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData = response.data.map(
        (messageData: Partial<MessageAPIResponse>) => Object.assign(
          new MessageAPIResponse(),
          messageData
        ).deserialize()
      );
      return PaginatedResponse.from<Message>(
        responseData,
        response.headers['x-pagination']
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error instanceof CanceledError)
        throw error;
      if (error.response?.status === HttpStatusCode.BadRequest)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedForumAccessException(forum.id);
      if (error instanceof CanceledError)
        throw error;
      throw new MessagingAPIServiceError(
        "getMessagesByForum",
        error
      );
    }
  }

  async getForumSubscriberInfo(forum: Forum, abortController?: AbortController): Promise<Individual[]> {
    if (!forum.id) throw new MessagingAPIServiceError(
      "getForumSubscriberInfo",
      new Error("Forum must have an id to get subscribers")
    );
    const url = new URL(
      `/messaging/forums/${forum.id.value}/subscribers`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData: MarketplaceSubscriberInfoAPIResponse[] =
        response.data.map((info: Partial<MarketplaceSubscriberInfoAPIResponse>) => {
          return Object.assign(
            new MarketplaceSubscriberInfoAPIResponse(),
            info
          );
        });
      return responseData.map((info) => info.deserialize());
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error instanceof CanceledError) {
        throw error;
      }
      throw new MessagingAPIServiceError(
        "getForumSubscriberInfo",
        error
      );
    }
  }

  async getMessageImages(
    message: Message,
    abortController?: AbortController
  ): Promise<MessageFileInfo[]> {
    if (!message.id || !message.forum?.id)
      throw new MessagingAPIServiceError(
        "getMessageImages",
        new Error("Message must have an id and forum id to get images")
      );
    const url = new URL(
      `/messaging/forums/${message.forum.id.value}/messages/${message.id.value}/images`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      return response.data.map((info: Partial<MessageFileInfoAPIResponse>) => {
        return Object.assign(
          new MessageFileInfoAPIResponse(),
          info
        ).deserialize();
      });
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedForumAccessException(message.forum.id);
      if (error instanceof CanceledError) {
        throw error;
      }
      throw new MessagingAPIServiceError(
        "getMessageImages",
        error
      );
    }
  }

  async getMessageAttachmentById(
    forumId: Guid | undefined,
    fileId: Guid,
    abortController?: AbortController
  ): Promise<AxiosResponse> {
    if (!forumId) throw new MessagingAPIServiceError(
      "getMessageAttachmentById",
      new Error("Must have a forum id to get attachments")
    );
    const url = new URL(
      `/messaging/forums/${forumId}/attachments/${fileId.value}`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      return await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          responseType: 'blob',
          signal: abortController?.signal
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedForumAccessException(forumId);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "getMessageAttachmentByIndex",
        error
      );
    }
  }

  async getAttachmentsByForum(
    forum: Forum,
    query: AttachmentQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<MessageFileInfo>> {
    if (!forum.id) throw new MessagingAPIServiceError(
      "GetAttachmentsByForum",
      new Error("Forum must have an id to get attachments")
    );

    try {
      const url = new URL(
        `/messaging/forums/${forum.id.value}/attachments`,
        AttorneyHubAPIService.apiBaseUrl
      );
      query.asSearchParams().forEach((value, key) => {
        url.searchParams.append(key, value);
      });
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData = response.data.map(
        (info: Partial<MessageFileInfoAPIResponse>) => Object.assign(
          new MessageFileInfoAPIResponse(),
          info
        ).deserialize()
      );
      return PaginatedResponse.from<MessageFileInfo>(
        responseData,
        response.headers['x-pagination']
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error instanceof CanceledError)
        throw error;
      if (error.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedForumAccessException(forum.id);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      if (error instanceof CanceledError) {
        throw error;
      }
      throw new MessagingAPIServiceError(
        "GetAttachmentsByForum",
        error
      );
    }
  }

  async createMessage(message: Message): Promise<Message> {
    if (!message.forum?.id) throw new MessagingAPIServiceError(
      "createMessage",
      new Error("Message must have a forum id to be created")
    );
    const request = new CreateMessageAPIRequest(message);
    const url = new URL(
      `/messaging/forums/${message.forum.id.value}/messages`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
        url.toString(),
        request.formData,
        {
          headers: this.headersWithFormData()
        }
      );
      const responseData: MessageAPIResponse = Object.assign(
        new MessageAPIResponse(),
        response.data
      );
      return responseData.deserialize();
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "createMessage",
        error
      );
    }
  }

  async createBulkMessages(messages: Message[]): Promise<Message[]> {
    if (!messages.length) {
      console.warn("No messages to create");
      return messages;
    }
    const request = new BulkMessageAPIRequest(messages);
    const url = new URL(
      `/messaging/bulk-messages`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
        url.toString(),
        request.payload,
        {
          headers: this.headersWithJson()
        }
      );
      const responseData: MessageAPIResponse[] = response.data.map(
        (messageData: MessageAPIResponse) =>
          Object.assign(
            new MessageAPIResponse(),
            messageData
          )
      );
      return responseData.map((messageData) => messageData.deserialize());
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "createBulkMessages",
        error
      );
    }
  }

  async createForum(forum: Forum): Promise<Forum> {
    const request: ForumAPIRequest = new ForumAPIRequest(forum);
    const url = new URL(
      "/messaging/forums",
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
        url.toString(),
        request,
        {
          headers: this.headersWithJson()
        }
      );
      const responseData: ForumAPIResponse = Object.assign(
        new ForumAPIResponse(),
        response.data
      );
      return responseData.deserialize();
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "createForum",
        error
      );
    }
  }

  async updateForum(_forum: Forum): Promise<Forum> {
    throw new MessagingAPIServiceError(
      "updateForum",
      new Error("Method not implemented.")
    );
  }

  async resolveForum(forum: Forum) {
    if (!forum.id) throw new MessagingAPIServiceError(
      "resolveForum",
      new Error("Forum must have an id to be resolved")
    );
    const url = new URL(
      `/messaging/forums/${forum.id.value}/resolve`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "resolveForum",
        error
      );
    }
  }

  async reopenForum(forum: Forum) {
    if (!forum.id) throw new MessagingAPIServiceError(
      "reopenForum",
      new Error("Forum must have an id to be reopened")
    );
    const url = new URL(
      `/messaging/forums/${forum.id.value}/reopen`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "reopenForum",
        error
      );
    }
  }

  async updateSubscribersForForum(forum: Forum) {
    if (!forum.id) throw new MessagingAPIServiceError(
      "updateSubscribersForForum",
      new Error("Forum must have an id to update subscribers")
    );
    const url = new URL(
      `/messaging/forums/${forum.id.value}/subscribers`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.put(
        url.toString(),
        {
          subscribers: forum.subscribers.map((subscriber) => new SubscriberAPIRequest(subscriber))
        },
        {
          headers: this.headersWithJson()
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "updateSubscribersForForum",
        error
      );
    }
  }

  async inviteSubscriberToForum(
    forum: Forum,
    invitee: Guid,
    invitationMessage?: string
  ) {
    if (!forum.id) throw new MessagingAPIServiceError(
      "inviteSubscriberToForum",
      new Error("Forum must have an id to invite a subscriber")
    );
    const url = new URL(
      `/messaging/forums/${forum.id.value}/invite-subscriber`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.post(
        url.toString(),
        new ForumSubscriberInvitationApiRequest(
          invitee,
          invitationMessage
        ),
        {
          headers: this.headersWithJson()
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "inviteSubscriberToForum",
        error
      );
    }
  }

  async acceptForumInvitation(forumInvitation: ForumInvitation) {
    const url = new URL(
      `/messaging/forum-invitations/${forumInvitation.id.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumInvitationDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "acceptForumInvitation",
        error
      );
    }
  }

  async deleteMessage(message: Message) {
    if (!message.id)
      throw new MessagingAPIServiceError(
        "deleteMessage",
        new Error("Message must have an id to be deleted")
      );
    if (!message.forum?.id)
      throw new MessagingAPIServiceError(
        "deleteMessage",
        new Error("Message must have a forum id to be deleted")
      );
    const url = new URL(
      `/messaging/forums/${message.forum.id.value}/messages/${message.id.value}`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.delete(
        url.toString(),
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new MessageNotFoundError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "deleteMessage",
        error
      );
    }
  }

  async deleteForum(forum: Forum) {
    if (!forum.id)
      throw new MessagingAPIServiceError(
        "deleteForum",
        new Error("Forum must have an id to be deleted")
      );

    const url = new URL(
      `/messaging/forums/${forum.id.value}`,
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      await axios.delete(
        url.toString(),
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound)
        throw new ForumDoesNotExistError(String(error.response.data));
      throw new MessagingAPIServiceError(
        "deleteForum",
        error
      );
    }
  }
}

export class MessageQuery {
  messageParameters: MessageParameters;
  paginationParameters: PaginationParameters;

  constructor(
    messageParameters: MessageParameters,
    paginationParameters: PaginationParameters
  ) {
    this.paginationParameters = paginationParameters;
    this.messageParameters = messageParameters;
  }

  public asSearchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.paginationParameters.asSearchParams(),
      ...this.messageParameters.asSearchParams()
    ]);
  }

  isEqualTo(other: MessageQuery): boolean {
    if (!other) {
      return false;
    }
    return this.messageParameters.isEqualTo((other.messageParameters)) &&
      this.paginationParameters.isEqualTo((other.paginationParameters));
  }
}

export class AttachmentQuery {
  attachmentParameters: AttachmentParameters;
  paginationParameters: PaginationParameters;

  constructor(
    attachmentParameters: AttachmentParameters,
    paginationParameters: PaginationParameters
  ) {
    this.paginationParameters = paginationParameters;
    this.attachmentParameters = attachmentParameters;
  }

  public asSearchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.paginationParameters.asSearchParams(),
      ...this.attachmentParameters.asSearchParams()
    ]);
  }

  isEqualTo(other: AttachmentQuery): boolean {
    if (!other) {
      return false;
    }
    return this.attachmentParameters.isEqualTo((other.attachmentParameters)) &&
      this.paginationParameters.isEqualTo((other.paginationParameters));
  }
}

export class MessagingAPIServiceError extends Error {
  method: string;
  error: Error;

  constructor(method: string, error: Error) {
    super(`Error calling ${method}: ${error}`);
    this.method = method;
    this.error = error;
  }
}

export class MessageNotFoundError extends Error {
  constructor(messageId: string) {
    super(`Message ${messageId} not found`);
  }
}

export class AttachmentNotFoundError extends Error {
  constructor(url: string) {
    super(`Attachment(s) not found at ${url}`);
  }
}
